<template>
  <div class="dynamic-page">
    <header class="hero">
      <div class="eyebrow">Dynamic payment link</div>
      <h1 class="hero-title">Pay {{ displayName }}</h1>
      <p class="hero-subtitle">
        Enter the amount you want to pay. We will generate a secure payment link and open it in a new tab.
      </p>
      <div class="hero-meta">
        <span class="meta-chip">
          <v-icon size="16">mdi-store-outline</v-icon>
          Branch ID: {{ config.branchID || 'missing' }}
        </span>
        <span class="meta-chip">
          <v-icon size="16">mdi-account-circle</v-icon>
          Shared by: {{ showSharedBy }}
        </span>
        <span class="meta-chip">
          <v-icon size="16">mdi-tag-text-outline</v-icon>
          Reference: {{ displayReference }}
        </span>
        <span v-if="isLytningStore && config.debtorId" class="meta-chip success">
          <v-icon size="16">mdi-account-check</v-icon>
          Debtor linked: {{ config.debtorId }}
        </span>
      </div>
    </header>

    <section v-if="isLytningStore && config.debtorId" class="customer-card">
      <div class="customer-header">
        <div>
          <div class="customer-chip">Linked customer</div>
          <h2 class="customer-name">{{ debtorName || `Debtor #${config.debtorId}` }}</h2>
          <p class="customer-subtitle">
            {{ debtorLoading ? 'Fetching customer details...' : 'Payments from this link will post to this account.' }}
          </p>
          <p v-if="debtorError" class="customer-error">{{ debtorError }}</p>
        </div>
        <button
          class="ghost-button"
          @click="printStatement"
          :disabled="!canPrintStatement || debtorLoading"
        >
          <v-icon size="18">mdi-printer</v-icon>
          Print statement
        </button>
      </div>
      <div v-if="debtorInfo" class="customer-grid">
        <div class="info-pill">
          <span class="pill-label">Name</span>
          <span class="pill-value">{{ debtorName }}</span>
        </div>
        <div class="info-pill">
          <span class="pill-label">TPIN</span>
          <span class="pill-value">{{ debtorTpin || 'N/A' }}</span>
        </div>
        <div class="info-pill">
          <span class="pill-label">Account balance</span>
          <span class="pill-value">{{ debtorBalance || 'N/A' }}</span>
        </div>
      </div>
      <div v-else-if="debtorLoading" class="customer-loading">
        <v-icon class="spin" size="18">mdi-loading</v-icon>
        <span>Loading customer info...</span>
      </div>
    </section>

    <section class="card">
      <div class="card-header">
        <div>
          <h2 class="card-title">Enter amount to pay</h2>
          <p class="card-subtitle">
            Payments are tagged to this branch and user so they reconcile automatically.
          </p>
        </div>
        <div v-if="!hasConfig" class="config-warning">
          <v-icon size="18" color="error">mdi-alert</v-icon>
          This link is missing setup details. Ask the merchant for a new link.
        </div>
      </div>

      <form class="form" @submit.prevent="generatePaymentLink">
        <label class="form-label">Amount</label>
        <div class="amount-input">
          <span class="currency">K</span>
          <input
            v-model.number="amount"
            type="number"
            min="0.01"
            step="0.01"
            placeholder="0.00"
            class="amount-field"
            :disabled="loading"
          />
        </div>
        <div class="form-hint">Minimum amount is K0.01</div>

        <label class="form-label">Note (optional)</label>
        <input
          v-model="note"
          type="text"
          class="text-field"
          placeholder="Add a short note for the payment"
          :disabled="loading"
        />

        <button class="primary-button" type="submit" :disabled="loading || !hasConfig">
          <div v-if="loading" class="loading">
            <v-icon class="spin" size="20">mdi-loading</v-icon>
            Generating link...
          </div>
          <div v-else class="button-content">
            <v-icon size="20">mdi-flash</v-icon>
            Generate & open payment link
          </div>
        </button>
        <p class="fine-print">We will automatically open the payment page once the link is ready.</p>
      </form>

      <div v-if="error" class="error-banner">
        <v-icon size="18" color="error">mdi-alert-circle</v-icon>
        <span>{{ error }}</span>
      </div>

      <div v-if="paymentLink?.url" class="result">
        <div class="result-header">
          <v-icon size="20" color="success">mdi-check-circle</v-icon>
          <div>
            <div class="result-title">Payment link ready</div>
            <div class="result-subtitle">Share or scan to pay {{ amountDisplay }}</div>
          </div>
        </div>
        <div class="result-body">
          <div class="qr-box" v-if="qrImage">
            <img :src="qrImage" alt="Payment QR code" />
            <div class="qr-hint">Scan to open the payment page with your amount.</div>
          </div>
          <div class="link-box">
            <label class="form-label">Payment link</label>
            <input v-model="paymentLink.url" readonly class="text-field" />
            <div class="link-actions">
              <button class="ghost-button" @click="shareLink(paymentLink.url)">
                <v-icon size="18">mdi-share-variant</v-icon>
                Share
              </button>
              <button class="ghost-button" @click="copyLink">
                <v-icon size="18">mdi-content-copy</v-icon>
                {{ copied ? 'Copied' : 'Copy link' }}
              </button>
              <button class="ghost-button" @click="openLink">
                <v-icon size="18">mdi-open-in-new</v-icon>
                Open link
              </button>
              <button class="ghost-button" @click="showPreview = true">
                <v-icon size="18">mdi-eye</v-icon>
                Preview
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="showPreview" class="modal-overlay" @click="showPreview = false">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Payment page preview</h3>
          <button class="close" @click="showPreview = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <iframe v-if="paymentLink?.url" :src="paymentLink.url" frameborder="0"></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { resolveStoreDatabase } from '@/services/branch-helpers';

export default {
  name: 'DynamicPaymentPage',
  data() {
    return {
      amount: '',
      note: '',
      loading: false,
      error: null,
      paymentLink: null,
      copied: false,
      showPreview: false,
      config: {
        branchID: null,
        user: '',
        reference: '',
        service: 'Payment',
        apiKey: '',
        business: '',
        storeId: null,
        debtorId: ''
      },
      storeDatabase: null,
      debtorInfo: null,
      debtorLoading: false,
      debtorError: null
    };
  },
  computed: {
    hasConfig() {
      return !!(this.config.branchID && this.config.user && this.config.apiKey);
    },
    displayName() {
      return 'Payment';
    },
    amountDisplay() {
      return this.amount ? `K${Number(this.amount).toFixed(2)}` : 'your amount';
    },
    showSharedBy() {
      return this.config.user || 'Unknown sender';
    },
    successUrl() {
      const origin = window?.location?.origin;
      const fallback = 'https://app.cash.co.zm';
      return `${origin && origin.startsWith('http') ? origin : fallback}/payments/success`;
    },
    qrImage() {
      return this.paymentLink?.url
        ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(
            this.paymentLink.url
          )}`
        : '';
    },
    isLytningStore() {
      return !!this.storeDatabase;
    },
    displayReference() {
      if (this.config.reference) return this.config.reference;
      return `payment-${new Date().toISOString().slice(0, 10)}`;
    },
    statementRange() {
      const year = new Date().getFullYear();
      const format = (month, day) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return {
        from: format(1, 1),
        to: format(12, 31)
      };
    },
    debtorName() {
      return this.debtorInfo?.Name || this.debtorInfo?.name || '';
    },
    debtorTpin() {
      return this.debtorInfo?.tpin || this.debtorInfo?.TPIN || '';
    },
    debtorBalance() {
      if (!this.debtorInfo) return '';
      const raw =
        this.debtorInfo.AccountBalance ??
        this.debtorInfo.accountBalance ??
        this.debtorInfo.Balance ??
        this.debtorInfo.balance;
      if (raw === undefined || raw === null || raw === '') return '';
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) return `K${parsed.toFixed(2)}`;
      return raw;
    },
    canPrintStatement() {
      return !!(this.isLytningStore && this.config.debtorId && this.storeDatabase);
    }
  },
  async mounted() {
    this.loadConfigFromQuery();
    if (this.config.storeId) {
      this.storeDatabase = await resolveStoreDatabase(this.config.storeId);
    }
    if (this.config.debtorId && this.storeDatabase) {
      await this.fetchDebtorInfo();
    }
  },
  methods: {
    loadConfigFromQuery() {
      const query = this.$route?.query || {};
      if (query.payload) {
        try {
          const decoded = JSON.parse(atob(atob(query.payload)));
          this.config.branchID = decoded.branchId || decoded.branchID || null;
          this.config.user = decoded.user || '';
          this.config.reference = decoded.ref || decoded.reference || '';
          this.config.apiKey = decoded.apiKey || '';
          this.config.business = decoded.business || decoded.store || '';
          this.config.storeId = decoded.storeId ? parseInt(decoded.storeId, 10) : null;
          this.config.debtorId = decoded.debtorId || decoded.debId || '';
        } catch (err) {
          console.error('Failed to decode dynamic payment payload', err);
          this.error = 'Link is invalid or expired. Please request a new link.';
        }
      } else {
        this.config.branchID = query.branchId ? parseInt(query.branchId, 10) : null;
        this.config.user = query.user || query.username || '';
        this.config.reference = query.ref || query.reference || '';
        this.config.apiKey = query.apiKey || '';
        this.config.business = query.business || query.store || '';
        this.config.storeId = query.storeId ? parseInt(query.storeId, 10) : null;
        this.config.debtorId = query.debtorId || query.debId || '';
      }
    },
    async fetchDebtorInfo() {
      if (!this.storeDatabase || !this.config.debtorId) return;
      this.debtorLoading = true;
      this.debtorError = null;
      try {
        const url = `https://lytningtomcat.chrilantech.com/OnlineSalesServer/debtor/${this.storeDatabase}/${this.config.debtorId}`;
        const { data } = await axios.get(url);
        this.debtorInfo = data;
      } catch (err) {
        console.error('DynamicPaymentPage: failed to load debtor info', err);
        this.debtorError = 'Unable to load customer details right now.';
        this.debtorInfo = null;
      } finally {
        this.debtorLoading = false;
      }
    },
    printStatement() {
      if (!this.canPrintStatement) return;
      const { from, to } = this.statementRange;
      const url = `https://lytningtomcat.chrilantech.com/PrintApi/debtor/${this.storeDatabase}/getMinistatement?from=${from}&to=${to}&id=${this.config.debtorId}&User=Auto`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async generatePaymentLink() {
      this.error = null;
      this.paymentLink = null;

      if (!this.hasConfig) {
        this.error = 'This payment link is missing required details. Please contact the sender.';
        return;
      }

      const cleanAmount = Number(this.amount);
      if (!cleanAmount || cleanAmount <= 0) {
        this.error = 'Enter an amount greater than zero.';
        return;
      }

      const baseReason = `Payment - ${this.displayReference}`;
      const reason = this.note ? `${baseReason} - ${this.note}` : baseReason;

      const payload = {
        amount: cleanAmount,
        Service_Description: 'Payment',
        reason,
        user: this.config.user,
        branchID: this.config.branchID,
        store_id: this.config.storeId,
        Cancel_Url: this.successUrl,
        Sucess_Url: this.successUrl,
        API_KEY: this.config.apiKey
      };

      // Attach debtor payment callback for Lytning stores
      if (this.isLytningStore && this.config.debtorId) {
        const depositPayload = {
          debId: this.config.debtorId,
          PayedAmount: cleanAmount,
          Des: reason,
          PaidFrom: 'Integrated Kazang Account',
          datPay: new Date().toISOString().slice(0, 10),
          Cashier: false,
          User: this.config.user
        };
        payload.depositMethod = 'POST';
        payload.depositUrl = `https://lytningtomcat.chrilantech.com/Lyt-SoftBackOfficeApi/debtor/${this.storeDatabase}/makePayment`;
        payload.depositPayload = JSON.stringify(depositPayload);
      }

      this.loading = true;
      try {
        const response = await this.$store.dispatch('step1', payload);
        this.paymentLink = response;

        if (response?.url) {
          window.open(response.url, '_blank', 'noopener,noreferrer');
        }
      } catch (err) {
        const message = err?.response?.data?.message || err?.message || 'Unable to generate payment link.';
        this.error = message;
        console.error('DynamicPaymentPage: failed to generate link', err);
      } finally {
        this.loading = false;
      }
    },
    copyLink() {
      if (!this.paymentLink?.url) return;
      navigator.clipboard
        .writeText(this.paymentLink.url)
        .then(() => {
          this.copied = true;
          setTimeout(() => (this.copied = false), 2000);
        })
        .catch((err) => {
          console.error('DynamicPaymentPage: copy failed', err);
          this.error = 'Could not copy the link. Please copy it manually.';
        });
    },
    openLink() {
      if (!this.paymentLink?.url) return;
      window.open(this.paymentLink.url, '_blank', 'noopener,noreferrer');
    },
    async shareLink(url) {
      if (!url) return;
      try {
        const sharePayload = {
          title: 'Payment link',
          text: `Pay ${this.showSharedBy} - ${this.displayReference}`,
          url
        };
        // Prefer native share if available
        if (navigator.share) {
          await navigator.share(sharePayload);
        } else if (window?.Capacitor?.Plugins?.Share) {
          await window.Capacitor.Plugins.Share.share(sharePayload);
        } else {
          await navigator.clipboard.writeText(url);
          this.copied = true;
          setTimeout(() => (this.copied = false), 2000);
        }
      } catch (err) {
        console.error('DynamicPaymentPage: share failed, copying instead', err);
        try {
          await navigator.clipboard.writeText(url);
          this.copied = true;
          setTimeout(() => (this.copied = false), 2000);
        } catch (copyErr) {
          console.error('DynamicPaymentPage: copy fallback failed', copyErr);
          this.error = 'Unable to share or copy the link.';
        }
      }
    }
  }
};
</script>

<style scoped>
.dynamic-page {
  min-height: 100vh;
  padding: 2rem 1.5rem 3rem;
  background: linear-gradient(145deg, #0d1b2a, #102a43);
  color: #f5f7fa;
}

.hero {
  max-width: 900px;
  margin: 0 auto 1.5rem;
}

.customer-card {
  max-width: 900px;
  margin: 0 auto 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25);
}

.customer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.customer-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 0.8rem;
  letter-spacing: 0.02em;
}

.customer-name {
  margin: 0.4rem 0 0.1rem;
}

.customer-subtitle {
  margin: 0;
  color: rgba(245, 247, 250, 0.75);
}

.customer-grid {
  margin-top: 0.75rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.65rem;
}

.info-pill {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.pill-label {
  font-size: 0.85rem;
  color: rgba(245, 247, 250, 0.72);
}

.pill-value {
  font-weight: 700;
  font-size: 1.05rem;
}

.customer-loading {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(245, 247, 250, 0.85);
  margin-top: 0.5rem;
}

.customer-error {
  color: #ffcdd2;
  margin: 0.35rem 0 0;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  font-size: 0.8rem;
  letter-spacing: 0.02em;
}

.hero-title {
  margin: 0.5rem 0 0.25rem;
  font-size: 2rem;
  font-weight: 700;
}

.hero-subtitle {
  margin: 0;
  max-width: 680px;
  color: rgba(245, 247, 250, 0.78);
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  font-size: 0.85rem;
}
.meta-chip.success {
  background: rgba(46, 204, 113, 0.12);
  border-color: rgba(46, 204, 113, 0.35);
  color: #b9f6ca;
}

.card {
  max-width: 900px;
  margin: 0 auto;
  background: #0b1624;
  border-radius: 16px;
  padding: 1.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.card-title {
  margin: 0;
  font-size: 1.25rem;
}

.card-subtitle {
  margin: 0.25rem 0 0;
  color: rgba(245, 247, 250, 0.76);
}

.config-warning {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  background: rgba(229, 57, 53, 0.12);
  border: 1px solid rgba(229, 57, 53, 0.35);
  border-radius: 10px;
  color: #ffcdd2;
  font-size: 0.9rem;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-label {
  font-weight: 600;
  margin-top: 0.35rem;
}

.amount-input {
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  padding: 0.1rem 0.5rem 0.1rem 0.25rem;
  background: rgba(255, 255, 255, 0.04);
}

.currency {
  font-weight: 600;
  padding: 0 0.75rem;
  color: rgba(245, 247, 250, 0.9);
}

.amount-field {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #f5f7fa;
  font-size: 1.25rem;
  padding: 0.75rem 0.5rem;
}

.amount-field::placeholder,
.text-field::placeholder {
  color: rgba(245, 247, 250, 0.45);
}

.text-field {
  width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  padding: 0.75rem 0.9rem;
  color: #f5f7fa;
  outline: none;
}

.form-hint {
  font-size: 0.9rem;
  color: rgba(245, 247, 250, 0.7);
}

.primary-button {
  margin-top: 0.75rem;
  border: none;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  background: linear-gradient(120deg, #1f8ef1, #5a43f1);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.primary-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.primary-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(31, 142, 241, 0.35);
}

.loading {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.spin {
  animation: spin 1s linear infinite;
}

.fine-print {
  margin: 0.35rem 0 0;
  color: rgba(245, 247, 250, 0.6);
  font-size: 0.9rem;
}

.error-banner {
  margin-top: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.9rem;
  background: rgba(229, 57, 53, 0.12);
  border: 1px solid rgba(229, 57, 53, 0.35);
  border-radius: 10px;
  color: #ffcdd2;
}

.result {
  margin-top: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1rem;
}

.result-title {
  font-weight: 700;
}

.result-subtitle {
  color: rgba(245, 247, 250, 0.75);
}

.result-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
  align-items: center;
}

.qr-box {
  border: 1px dashed rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.04);
}

.qr-box img {
  width: 220px;
  max-width: 100%;
  border-radius: 10px;
  background: #fff;
  padding: 0.5rem;
}

.qr-hint {
  margin-top: 0.5rem;
  color: rgba(245, 247, 250, 0.72);
  font-size: 0.9rem;
}

.link-box {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.link-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ghost-button {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 0.9rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: transparent;
  color: #f5f7fa;
  cursor: pointer;
}

.ghost-button:hover {
  background: rgba(255, 255, 255, 0.06);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1100;
}

.modal {
  background: #0b1624;
  border-radius: 12px;
  width: min(100%, 1100px);
  height: 75vh;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.12);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.modal iframe {
  flex: 1;
  border: none;
}

.close {
  border: none;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  color: #f5f7fa;
  cursor: pointer;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 720px) {
  .dynamic-page {
    padding: 1.25rem 1rem 2.5rem;
  }

  .card {
    padding: 1.25rem;
  }

  .meta-chip {
    width: 100%;
  }
}
</style>
